<template>
  <div class="flex flex-wrap justify-center w-full bg-white">
    <div
      v-show="!completeData"
      class="p-5 mb-8 max-w-xs text-center"
    >
      <div class="my-8">
        <VIcon
          name="check-circle"
          :class="$style['icon-size']"
        />
      </div>

      <div class="text-grey-60 text-lg">
        <p>Datos guardados correctamente.</p>
        <p>Para completar tu reserva necesitamos que rellenes el siguiente formulario.</p>
      </div>

      <div class="px-4 mt-6">
        <VButton
          class="text-lg rounded"
          @click="completeData = true"
        >
          Completar datos
        </VButton>
      </div>
    </div>

    <div
      v-show="completeData"
      class="w-full md:p-12"
    >
      <div
        class="absolute pin-r leading-none cursor-pointer text-5xl md:hidden"
        :class="$style['close-position']"
        @click="close"
      >
        <VIcon name="close" />
      </div>

      <div class="w-full mb-6">
        <p class="py-2 px-6 text-lg font-bold">
          Necesitamos tus datos de contacto para confirmar tu reserva.
        </p>
      </div>

      <div :class="$style['form-content']">
        <div class="font-audi-type-normal px-5">
          <div class="max-w-2xs">
            <VTitleField
              ref="title"
              v-model="fields.title"
            />

            <VFirstNameField
              ref="firstName"
              v-model="fields.firstName"
              :back-validation-error="validationErrors['firstName'] || null"
            />

            <VLastNameField
              ref="lastName"
              v-model="fields.lastName"
              :back-validation-error="validationErrors['lastName'] || null"
            />

            <VSecondLastNameField
              ref="secondLastName"
              v-model="fields.secondLastName"
              :back-validation-error="validationErrors['secondLastName'] || null"
            />

            <VEmailField
              ref="email"
              v-model="fields.email"
              :back-validation-error="validationErrors['email'] || null"
            />

            <VPhoneField
              ref="phone"
              v-model="fields.phone"
              :back-validation-error="validationErrors['phone'] || null"
            />

            <VPostalCodeField
              ref="postalCode"
              v-model="fields.postalCode"
              :back-validation-error="validationErrors['postalCode'] || null"
            />

            <VDniField
              ref="dni"
              v-model="fields.dni"
              :back-validation-error="validationErrors['dni'] || null"
            />

            <div class="w-full mb-6">
              <p class="pb-2 text-lg font-bold">
                ¡Mantente al día!
              </p>
            </div>

            <VCheckCommercialField
              ref="checkCommercial"
              v-model="fields.checkCommercial"
              :back-validation-error="validationErrors['checkCommercial'] || null"
            />

            <VCheckProfileField
              ref="checkProfile"
              v-model="fields.checkProfile"
              :back-validation-error="validationErrors['checkProfile'] || null"
            />
          </div>

          <div
            v-if="backError"
            class="bg-danger-90 mt-8 px-3 py-2 text-sm text-danger-30 rounded-xl text-center"
          >
            {{ backError }}
          </div>

          <div class="mb-4 md:mb-0 mt-4 text-center">
            <VButton
              class="my-4 text-lg"
              :disabled="loading"
              :loading="loading"
              @click="onSubmit"
            >
              Confirmar datos
            </VButton>
          </div>

          <div
            class="mt-5 mb-8 text-base leading-tight text-grey-40"
            :class="$style['label-color']"
          >
            <p class="text-xs">
              Al enviar este formulario confirmas haber leído y aceptado nuestra
              <a
                href="#"
                target="_blank"
                class="text-primary-blue"
              >
                Política de Datos y Privacidad
              </a>.
            </p>
            <p class="mt-4 text-sm">
              <span class="text-lg pb-4 block">Para poder participar en los test drive de Audi, deberá cumplir los siguientes requisitos:</span>
              <ul class="ml-10">
                <li>Tener la mayoría de edad y un permiso de conducción válido y en vigor en Unión Europea o equivalente y no estar inhabilitado para conducir.</li>
                <li>Tener dos o más años de experiencia con el carnet de conducir y superar los 20 años de edad.</li>
                <li>Deberá llevar consigo su permiso de conducción original cuando realice la prueba de conducción y tenerlo disponible a su llegada. No se aceptarán fotocopias. Los permisos o licencias de conducción internacionales sólo serán válidos si vienen acompañados de un permiso de conducción nacional.</li>
              </ul>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'

import {
  EventBus
} from '@/utils'

import {
  VIcon,
  VButton
} from '@c/base'

import {
  VTitleField,
  VFirstNameField,
  VLastNameField,
  VSecondLastNameField,
  VEmailField,
  VPhoneField,
  VPostalCodeField,
  VDniField,
  VCheckCommercialField,
  VCheckProfileField
} from '@c/fields'

export default {
  components: {
    VIcon,
    VButton,
    VTitleField,
    VFirstNameField,
    VLastNameField,
    VSecondLastNameField,
    VEmailField,
    VPhoneField,
    VPostalCodeField,
    VDniField,
    VCheckCommercialField,
    VCheckProfileField
  },

  props: {
    activityToBook: {
      type: Object,
      required: true
    },

    concessionary: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      completeData: false,
      modalEl: null,
      loading: false,
      fields: {
        title: '',
        firstName: '',
        lastName: '',
        secondLastName: '',
        email: '',
        phone: '',
        postalCode: '',
        dni: '',
        checkCommercial: null,
        checkProfile: null,
        ...this.activityToBook.attendee
      },
      validationErrors: {},
      backError: ''
    }
  },

  created () {
    document.body.style.overflow = 'hidden'
  },

  mounted () {
    this.modalEl = document.getElementsByClassName('v--modal')[0]
  },

  beforeDestroy () {
    document.body.style.overflow = 'auto'
  },

  methods: {
    onSubmit () {
      if (this.hasValidationError() || this.loading) {
        return
      }

      this.backError = ''
      this.validationErrors = {}
      this.loading = true

      this.submit()
        .then(response => {
          this.$router.push({
            name: 'success',
            params: response.data.data
          })

          this.close()
        })
        .catch(error => {
          let errorData = error.response.data
          this.backError = errorData.message

          if (errorData.category && errorData.category === 'validation') {
            let completeKey = _.map(errorData.data, (value, key) => key)[0]
            let keySplitted = completeKey.split('.')
            let key = keySplitted[keySplitted.length - 1]

            if (key === 'workshops') {
              this.backError = 'Ha habido un error a la hora de procesar los datos de la actividad.'
            }

            _.set(this.validationErrors, _.camelCase(key), errorData.data[completeKey][0])
          }

          if (errorData.category && errorData.category === 'workshop_conflict') {
            EventBus.$emit('workshopConflict', {
              attendeeData: this.fields,
              errorData: errorData
            })

            this.close()
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    submit () {
      return axios.post('/api/activities/' + this.concessionary, {
        activityToBook: {
          attendee: _.mapKeys(this.fields, (v, k) => _.snakeCase(k)),
          workshops: this.activityToBook.workshops.map(workshop => _.mapKeys(workshop, (v, k) => _.snakeCase(k)))
        }
      })
    },

    close () {
      this.$emit('close')
    },

    isCheckField (field) {
      return field === 'checkCommercial' ||
             field === 'checkProfile'
    },

    hasValidationError () {
      let errors = []

      _.forEach(this.fields, (field, fieldKey) => {
        this.$refs[fieldKey].$v.$touch()

        if (this.$refs[fieldKey].$v[fieldKey].$error) {
          errors.push(fieldKey)
        }
      })

      return errors.length > 0
    }
  }
}
</script>

<style lang="scss" module>
.icon-size {
  font-size: 10rem;
}

.close-position {
  top: -50px;
}

.form-content {
  max-height: calc(100vh - 16rem);
  overflow-y: auto;
}

.select {
  height: 30px;
}

.label-color {
  color: #989898;
}
</style>
